import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { CheckCircle, Warning, X, Copy, Check } from "@phosphor-icons/react";

const fmt = (n, dec = 2) => n?.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtData = (s) => { if (!s) return "—"; const [a, m, d] = s.split("-"); return `${d}/${m}/${a}`; };

export default function Ptax() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ cliente: "", volume_usd: "", data_vencimento: "" });
  const [volumeDisplay, setVolumeDisplay] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [copied, setCopied] = useState(false);
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const [clienteSap, setClienteSap] = useState(null);
  const [sapQuery, setSapQuery] = useState("");
  const [sapResults, setSapResults] = useState([]);
  const [sapLoading, setSapLoading] = useState(false);
  const [sapNoResults, setSapNoResults] = useState(false);
  const debounceRef = useRef(null);
  const [cnpjIeMode, setCnpjIeMode] = useState(false);
  const [cnpjIeInput, setCnpjIeInput] = useState("");
  const [cnpjIeConfirmed, setCnpjIeConfirmed] = useState(null);
  const [tipoVencimento, setTipoVencimento] = useState("data_fixa");
  const [ddfDias, setDdfDias] = useState("");

  const handleSapSearch = (query) => {
    setSapQuery(query);
    setSapNoResults(false);
    if (clienteSap) { setClienteSap(null); setForm(f => ({ ...f, cliente: "" })); }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) { setSapResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSapLoading(true);
      try {
        const { data } = await api.get("/clientes/search", { params: { q: query.trim() } });
        setSapResults(data);
        setSapNoResults(data.length === 0);
      } catch { setSapResults([]); }
      finally { setSapLoading(false); }
    }, 300);
  };

  const handleSapSelect = (item) => {
    setClienteSap(item);
    setSapQuery(item.nome);
    setSapResults([]);
    setForm(f => ({ ...f, cliente: item.nome }));
  };

  const clearClienteSap = () => {
    setClienteSap(null);
    setSapQuery("");
    setSapResults([]);
    setSapNoResults(false);
    setForm(f => ({ ...f, cliente: "" }));
    setCnpjIeMode(false);
    setCnpjIeInput("");
    setCnpjIeConfirmed(null);
  };

  const handleSubmit = async () => {
    if (!form.cliente || !form.volume_usd) {
      toast.error("Preencha Cliente e Volume");
      return;
    }
    if (tipoVencimento === "data_fixa" && !form.data_vencimento) {
      toast.error("Preencha a data de vencimento");
      return;
    }
    if (tipoVencimento === "ddf" && (!ddfDias || Number(ddfDias) <= 0)) {
      toast.error("Preencha os dias após faturamento");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ptax", {
        cliente: form.cliente,
        volume_usd: Number(form.volume_usd),
        data_vencimento: tipoVencimento === "data_fixa" ? form.data_vencimento : null,
        ddf_dias: tipoVencimento === "ddf" ? Number(ddfDias) : null,
        tipo_vencimento: tipoVencimento,
      });
      setConfirmation(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar PTAX");
    } finally {
      setLoading(false);
    }
  };

  if (confirmation) {
    const travasUrl = user?.role === "vendedor" ? "/vendedor/travas"
      : user?.role === "gestao" ? "/hedge"
      : "/hedge/travas";
    const vencLabel = confirmation.tipo_vencimento === "ddf"
      ? `DDF · ${confirmation.ddf_dias} dias após faturamento`
      : `Data fixa · ${fmtData(confirmation.data_vencimento)}`;

    return (
      <Layout>
        <div className="p-6 md:p-8 max-w-2xl">
          <div className="border border-[#FFB800] bg-[#0B2D4D] rounded-sm p-8 space-y-6">
            <div className="flex items-center gap-3">
              <CheckCircle weight="fill" className="w-8 h-8 text-[#FFB800]" />
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#FFB800]">PTAX Registrada</p>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold font-mono-data cursor-pointer select-none" style={{ color: "#FFB800" }} onClick={() => handleCopy(confirmation.codigo)} title="Copiar código">{copied ? "Copiado!" : confirmation.codigo}</h2>
                  <button
                    type="button"
                    onClick={() => handleCopy(confirmation.codigo)}
                    className="text-[#6b89a8] hover:text-white transition-colors p-1"
                    title="Copiar código"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#FFB800]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider mb-1">Cliente</p>
                <p className="font-medium">{confirmation.cliente}</p>
              </div>
              <div>
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider mb-1">Volume USD</p>
                <p className="font-mono-data">USD {fmt(confirmation.volume_usd)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider mb-1">Tipo de vencimento</p>
                <p className="font-mono-data text-sm">{vencLabel}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-[#6b89a8] uppercase tracking-wider mb-1">Taxa</p>
                <p className="text-sm text-[#8aa9c9]">
                  Será definida pela PTAX de venda do dia anterior ao{" "}
                  {confirmation.tipo_vencimento === "ddf" ? "faturamento" : "vencimento"} (Banco Central do Brasil)
                </p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setConfirmation(null); setForm({ cliente: "", volume_usd: "", data_vencimento: "" }); setVolumeDisplay(""); setDdfDias(""); setTipoVencimento("data_fixa"); setClienteSap(null); setSapQuery(""); setSapResults([]); setSapNoResults(false); setCnpjIeMode(false); setCnpjIeInput(""); setCnpjIeConfirmed(null); }} data-testid="btn-nova-ptax">
                Nova PTAX
              </Button>
              <Button onClick={() => navigate(travasUrl)} data-testid="btn-ver-travas" className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
                Ver PTAXs pendentes →
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-5xl">
        <div>
          <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Vendedor · Tela PTAX</p>
          <h1 className="text-3xl font-bold tracking-tight">Pedido em Dólar — PTAX</h1>
        </div>

        <div className="border border-[#FFB800] rounded-sm p-6 space-y-5 bg-[#0B2D4D]">
          <div className="flex items-center gap-3">
            <Badge className="bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/30 border font-mono-data text-[10px] tracking-wider uppercase">PTAX Dia Anterior</Badge>
          </div>
          <p className="text-xs text-[#6b89a8]">A taxa será definida pela PTAX de venda do dia anterior ao vencimento (Banco Central do Brasil). Disponível fora do horário de operação.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="ptax-cliente" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Cliente</Label>
              <div className="relative">
                {clienteSap ? (
                  <div className="flex items-center justify-between border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm px-3 py-2 min-h-[40px]">
                    <div>
                      <p className="text-sm font-medium" style={{ color: "#FFB800" }}>{clienteSap.nome}</p>
                      <p className="text-[11px] text-[#6b89a8] font-mono-data">
                        {clienteSap.cnpj && <>{clienteSap.cnpj} · </>}BP {clienteSap.business_partner}
                      </p>
                    </div>
                    <button type="button" onClick={clearClienteSap} className="text-[#6b89a8] hover:text-white ml-3 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : cnpjIeConfirmed ? (
                  <div className="flex items-center justify-between border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm px-3 py-2 min-h-[40px]">
                    <div>
                      <p className="text-sm font-medium font-mono-data" style={{ color: "#FFB800" }}>{cnpjIeConfirmed}</p>
                      <p className="text-[11px] text-[#6b89a8]">Identificação manual</p>
                    </div>
                    <button type="button" onClick={clearClienteSap} className="text-[#6b89a8] hover:text-white ml-3 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : cnpjIeMode ? (
                  <div className="space-y-1.5">
                    <div className="flex gap-2 items-center">
                      <Input
                        autoFocus
                        autoComplete="off"
                        value={cnpjIeInput}
                        onChange={(e) => setCnpjIeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const raw = cnpjIeInput.trim();
                            if (!raw) return;
                            const digits = raw.replace(/\D/g, "");
                            const isCnpj = digits.length === 14;
                            const formatted = isCnpj ? `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}` : raw;
                            setCnpjIeConfirmed(`${isCnpj ? "CNPJ" : "IE"}: ${formatted}`);
                            setForm(f => ({ ...f, cliente: formatted }));
                          }
                          if (e.key === "Escape") { setCnpjIeMode(false); setCnpjIeInput(""); }
                        }}
                        placeholder="CNPJ ou Inscrição Estadual"
                        className="bg-transparent border-[#FFB800]/30 focus-visible:ring-[#FFB800] flex-1"
                      />
                      <button type="button" onClick={() => { setCnpjIeMode(false); setCnpjIeInput(""); }} className="text-[#6b89a8] hover:text-white flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const raw = cnpjIeInput.trim();
                        if (!raw) return;
                        const digits = raw.replace(/\D/g, "");
                        const isCnpj = digits.length === 14;
                        const formatted = isCnpj ? `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}` : raw;
                        setCnpjIeConfirmed(`${isCnpj ? "CNPJ" : "IE"}: ${formatted}`);
                        setForm(f => ({ ...f, cliente: formatted }));
                      }}
                      className="text-xs text-[#FFB800] hover:underline"
                    >
                      Usar este identificador →
                    </button>
                  </div>
                ) : (
                  <>
                    <Input
                      id="ptax-cliente"
                      data-testid="input-ptax-cliente"
                      value={sapQuery}
                      autoComplete="off"
                      onChange={(e) => handleSapSearch(e.target.value)}
                      onBlur={() => setTimeout(() => { setSapResults([]); setSapNoResults(false); }, 200)}
                      placeholder="Nome do Cliente"
                      className="bg-transparent border-[#FFB800]/30 focus-visible:ring-[#FFB800] pr-8"
                    />
                    {sapLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-[#FFB800] border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {sapResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[#0B2D4D] border border-[#123a5e] rounded-sm shadow-xl max-h-64 overflow-y-auto">
                        {sapResults.map((item) => (
                          <div
                            key={item.business_partner}
                            className="px-3 py-2.5 hover:bg-[#FFB800]/10 cursor-pointer border-b border-[#123a5e] last:border-0"
                            onMouseDown={() => handleSapSelect(item)}
                          >
                            <p className="text-sm text-white">{item.nome}</p>
                            <p className="text-[11px] text-[#6b89a8] font-mono-data">
                              {item.cnpj || "—"} · BP {item.business_partner}
                            </p>
                          </div>
                        ))}
                        <div
                          className="px-3 py-2.5 hover:bg-[#FFB800]/10 cursor-pointer border-t border-[#123a5e]"
                          onMouseDown={() => { setCnpjIeMode(true); setSapResults([]); setSapNoResults(false); }}
                        >
                          <p className="text-xs text-[#FFB800]">Não encontrado? Informar CNPJ/IE</p>
                        </div>
                      </div>
                    )}
                    {sapNoResults && (
                      <div className="absolute z-50 w-full mt-1 bg-[#0B2D4D] border border-[#123a5e] rounded-sm shadow-xl">
                        <div
                          className="px-3 py-2.5 hover:bg-[#FFB800]/10 cursor-pointer"
                          onMouseDown={() => { setCnpjIeMode(true); setSapNoResults(false); }}
                        >
                          <p className="text-xs text-[#6b89a8]">Nenhum cliente encontrado.</p>
                          <p className="text-xs text-[#FFB800] mt-0.5">Informar CNPJ/IE →</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ptax-volume" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Montante (USD)</Label>
              <Input id="ptax-volume" data-testid="input-ptax-volume" type="text" inputMode="decimal" value={volumeDisplay}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\./g, "").replace(/[^\d,]/g, "");
                  if (!cleaned) { setVolumeDisplay(""); setForm({ ...form, volume_usd: "" }); return; }
                  const comma = cleaned.indexOf(",");
                  if (comma === -1) {
                    const n = parseInt(cleaned, 10);
                    setVolumeDisplay(isNaN(n) ? "" : n.toLocaleString("pt-BR"));
                    setForm({ ...form, volume_usd: isNaN(n) ? "" : String(n) });
                  } else {
                    const intStr = cleaned.slice(0, comma);
                    const decStr = cleaned.slice(comma + 1).replace(/,/g, "");
                    const n = intStr ? parseInt(intStr, 10) : 0;
                    setVolumeDisplay(`${intStr ? n.toLocaleString("pt-BR") : "0"},${decStr}`);
                    setForm({ ...form, volume_usd: `${intStr || "0"}.${decStr}` });
                  }
                }}
                placeholder="0" className="bg-transparent border-[#FFB800]/30 font-mono-data focus-visible:ring-[#FFB800]" />
            </div>
            <div className="space-y-3 md:col-span-2">
              <Label className="text-xs tracking-wider uppercase text-[#8aa9c9]">Tipo de vencimento</Label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "data_fixa"} onChange={() => setTipoVencimento("data_fixa")} className="w-4 h-4 accent-[#FFB800]" />
                  <span className="text-sm">Data fixa</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tipo_vencimento" checked={tipoVencimento === "ddf"} onChange={() => setTipoVencimento("ddf")} className="w-4 h-4 accent-[#FFB800]" />
                  <span className="text-sm">DDF</span>
                </label>
              </div>
            </div>

            {tipoVencimento === "data_fixa" ? (
              <div className="space-y-2">
                <Label htmlFor="ptax-venc" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Data de vencimento</Label>
                <Input id="ptax-venc" data-testid="input-ptax-venc" type="date" value={form.data_vencimento}
                  onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })}
                  className="bg-transparent border-[#FFB800]/30 font-mono-data focus-visible:ring-[#FFB800]" />
              </div>
            ) : (
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ptax-ddf" className="text-xs tracking-wider uppercase text-[#8aa9c9]">Dias após faturamento</Label>
                <div className="flex gap-2 items-center">
                  <Input id="ptax-ddf" data-testid="input-ptax-ddf" type="number" min="1" value={ddfDias}
                    onChange={(e) => setDdfDias(e.target.value)}
                    placeholder="Ex: 90" className="bg-transparent border-[#FFB800]/30 font-mono-data focus-visible:ring-[#FFB800] w-32" />
                  <div className="flex gap-2">
                    {[30, 60, 90, 120].map((d) => (
                      <button key={d} type="button" onClick={() => setDdfDias(String(d))}
                        className={`px-3 py-1.5 text-xs border rounded-sm font-mono-data transition-colors ${String(ddfDias) === String(d) ? "bg-[#FFB800] text-black border-[#FFB800]" : "border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm p-3 flex gap-2 text-xs text-[#8aa9c9] mt-1">
                  <Warning weight="fill" className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                  Pedido DDF: a taxa de câmbio será a PTAX de venda do dia anterior ao faturamento (não ao pagamento). Quando a nota for emitida em Paranaguá, a taxa já estará definida pela PTAX do dia anterior.
                </div>
              </div>
            )}
          </div>
          <Button type="button" onClick={handleSubmit} disabled={loading} data-testid="btn-registrar-ptax"
            className="bg-[#FFB800] text-black hover:bg-[#e0a600] font-semibold">
            {loading ? "Registrando..." : "Registrar PTAX"}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
